import React from 'react';

export interface FeatureItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  layout?: 'horizontal' | 'vertical';
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'coral'
    | 'neutral'
    | 'problem'
    | 'solution'
    | 'success';
  isCard?: boolean;
  titleColor?: 'current' | 'primary' | 'secondary' | 'tertiary';
  titleFontSize?: 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  minHeight?: string | number;
  iconSize?: string;
  removeIconBackground?: boolean;
  disableHover?: boolean;
  hoverBgOnly?: boolean;
}

export function FeatureItem({
  icon,
  title,
  description,
  layout = 'horizontal',
  variant = 'neutral',
  isCard = false,
  titleColor = 'current',
  titleFontSize = 'lg',
  minHeight,
  iconSize,
  removeIconBackground = false,
  disableHover = false,
  hoverBgOnly = false,
}: FeatureItemProps) {
  const variantStyles = {
    primary: {
      icon: 'bg-primary/10 text-primary',
      card: 'border-primary/20 bg-primary/[0.02]',
      text: 'text-primary',
    },
    secondary: {
      icon: 'bg-secondary/10 text-secondary',
      card: 'border-secondary/20 bg-secondary/[0.02]',
      text: 'text-secondary',
    },
    tertiary: {
      icon: 'bg-tertiary/10 text-tertiary',
      card: 'border-tertiary/20 bg-tertiary/[0.02]',
      text: 'text-tertiary',
    },
    coral: {
      icon: 'bg-coral/10 text-coral',
      card: 'border-coral/20 bg-coral/[0.02]',
      text: 'text-coral',
    },
    neutral: {
      icon: 'bg-slate-50 border border-slate-200 text-slate-700',
      card: 'border-slate-100 bg-white/90',
      text: 'text-slate-900',
    },
    problem: {
      icon: 'bg-slate-50 border border-slate-200 text-slate-500',
      card: 'border-slate-200 bg-slate-50/80',
      text: 'text-slate-600',
    },
    solution: {
      icon:
        'bg-gradient-to-br from-secondary/20 to-tertiary/20 text-primary ring-1 ring-secondary/30',
      card:
        'border-secondary/30 bg-gradient-to-br from-primary/[0.04] via-white to-secondary/[0.08] ring-1 ring-secondary/10',
      text: 'text-primary',
    },
    success: {
      icon: 'bg-tertiary/10 text-tertiary',
      card: 'border-tertiary/20 bg-tertiary/[0.02]',
      text: 'text-tertiary',
    },
  };

  const currentVariant =
    variantStyles[variant as keyof typeof variantStyles] || variantStyles.neutral;

  // Styles for the icon container
  const defaultIconSize = layout === 'vertical' ? 'w-16 h-16 mb-6' : 'w-10 h-10';
  const finalIconSize = iconSize || defaultIconSize;

  const iconBgStyles = removeIconBackground
    ? currentVariant.text
    : `rounded-full shadow-sm ${currentVariant.icon}`;

  const iconContainerClass = `flex items-center justify-center ${finalIconSize} ${iconBgStyles}`;

  let titleColorClass = 'text-current';
  if (titleColor === 'primary') titleColorClass = 'text-primary';
  if (titleColor === 'secondary') titleColorClass = 'text-secondary';
  if (titleColor === 'tertiary') titleColorClass = 'text-tertiary';

  const titleSizeClass =
    {
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
    }[titleFontSize] || 'text-lg';

  // Wrapper styles
  const cardBgClass = hoverBgOnly ? 'bg-transparent hover:bg-slate-50' : currentVariant.card;
  const cardBorderClass = hoverBgOnly ? 'border-slate-200' : '';
  const cardShadowClass = hoverBgOnly ? 'shadow-none' : 'shadow-sm';
  const wrapperClass = isCard
    ? `h-full backdrop-blur-md p-6 rounded-lg border ${cardShadowClass} transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-1 hover:shadow-md ${cardBgClass} ${cardBorderClass}`
    : '';

  const contentHoverClass =
    !isCard && !disableHover ? 'transition-transform duration-300 hover:-translate-y-1' : '';

  const content =
    layout === 'vertical' ? (
      <div className={`flex flex-col items-center text-center relative z-10 ${contentHoverClass}`}>
        <div className={iconContainerClass}>{icon}</div>
        <h3
          className={`mb-2 font-bold ${titleSizeClass} ${
            titleColorClass === 'text-current' && isCard ? currentVariant.text : titleColorClass
          }`}
        >
          {title}
        </h3>
        <p className="text-slate-700 leading-relaxed max-w-[200px] mx-auto">{description}</p>
      </div>
    ) : (
      <div className={`flex gap-4 ${contentHoverClass}`}>
        <div className="shrink-0 flex items-start">
          <div className={iconContainerClass}>{icon}</div>
        </div>
        <div className="flex flex-col justify-start">
          <h4
            className={`font-bold flex items-center min-h-[40px] gap-2 ${titleSizeClass} ${
              titleColorClass === 'text-current' && isCard ? currentVariant.text : titleColorClass
            }`}
          >
            {title}
          </h4>
          <p className="text-slate-700 leading-relaxed mt-1">{description}</p>
        </div>
      </div>
    );

  return isCard ? (
    <div className={wrapperClass} style={{ minHeight }}>
      {content}
    </div>
  ) : (
    content
  );
}
